/**
 * News — /news
 * ════════════
 * Platform news, updates and announcements.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { useSEO } from '../utils/seo';
import Navbar from '../components/Navbar';
import './News.css';

function NewsCard({ item, types }) {
  const [expanded, setExpanded] = useState(false);
  const nt = types.find(t => t.id === item.newsType) || types[0];

  return (
    <div className={`nw-card ${item.isPinned ? 'pinned' : ''}`}>
      {item.isPinned && <div className="nw-pin">📌 Pinned</div>}

      <div className="nw-card-top">
        <span className="nw-emoji">{item.emoji || '📢'}</span>
        <div className="nw-card-info">
          <div className="nw-card-meta">
            <span className="nw-badge" style={{ color: nt.color, background: nt.bg }}>
              {nt.label}
            </span>
            <span className="nw-date">
              {new Date(item.date).toLocaleDateString('en-IN', {
                day: '2-digit', month: 'long', year: 'numeric',
              })}
            </span>
          </div>
          <h3 className="nw-title">{item.title}</h3>
          <p className="nw-summary">{item.summary}</p>

          {item.body && (
            <button className="nw-expand" onClick={() => setExpanded(p => !p)}>
              {expanded ? 'Show less ↑' : 'Read more ↓'}
            </button>
          )}
        </div>
      </div>

      {expanded && item.body && (
        <div
          className="nw-body"
          dangerouslySetInnerHTML={{ __html: item.body }}
        />
      )}
    </div>
  );
}

export default function News({ onSignIn }) {
  useSEO({
    title:       'Platform News & Updates',
    description: 'Latest DevBrains platform updates, new features, maintenance windows and announcements.',
    canonical:   '/news',
  });
  const { getPublishedNews, NEWS_TYPES } = useContent();
  const [activeType, setActiveType] = useState('all');

  const allNews = getPublishedNews();

  const filtered = activeType === 'all'
    ? allNews
    : allNews.filter(n => n.newsType === activeType);

  // Count per type
  const counts = {};
  allNews.forEach(n => { counts[n.newsType] = (counts[n.newsType] || 0) + 1; });

  return (
    <>
<Navbar onSignIn={onSignIn} />

      <div className="nw-page">

        {/* ── Hero ──────────────────────────────────────── */}
        <div className="nw-hero">
          <div className="nw-hero-inner">
            <div className="nw-hero-badge">📢 Platform News</div>
            <h1 className="nw-hero-title">What's New at DevBrains</h1>
            <p className="nw-hero-sub">
              Feature launches, platform updates, scheduled maintenance and
              important announcements — all in one place.
            </p>
          </div>
        </div>

        <div className="nw-content">

          {/* Type filter */}
          <div className="nw-filters">
            <button
              className={`nw-filter-pill ${activeType === 'all' ? 'active' : ''}`}
              onClick={() => setActiveType('all')}
            >
              All <span className="nw-filter-count">{allNews.length}</span>
            </button>
            {NEWS_TYPES.filter(t => counts[t.id]).map(t => (
              <button
                key={t.id}
                className={`nw-filter-pill ${activeType === t.id ? 'active' : ''}`}
                onClick={() => setActiveType(t.id)}
                style={activeType === t.id
                  ? { borderColor: t.color, color: t.color, background: t.bg }
                  : {}}
              >
                {t.label} <span className="nw-filter-count">{counts[t.id]}</span>
              </button>
            ))}
          </div>

          {/* News list */}
          {filtered.length === 0 ? (
            <div className="nw-empty">
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 700 }}>No news in this category yet</div>
            </div>
          ) : (
            <div className="nw-list">
              {filtered.map(item => (
                <NewsCard key={item.id} item={item} types={NEWS_TYPES} />
              ))}
            </div>
          )}

          {/* Training CTA */}
          <div className="nw-training-cta">
            <span>📚</span>
            <div>
              <div style={{ fontWeight: 800, color: '#111827', fontSize: '.9rem' }}>
                Looking for mentoring guides?
              </div>
              <div style={{ fontSize: '.78rem', color: '#6b7280', marginTop: 2 }}>
                Check out the Training Hub for tips on getting the most from your sessions.
              </div>
            </div>
            <Link to="/training" className="nw-training-link">View Guides →</Link>
          </div>

        </div>
      </div>
    </>
  );
}
